import React, { useEffect, useState } from "react";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import useSignIn from "../../hooks/auth/useSignIn";
import useKakaoLogin from "../../hooks/auth/useKakaoLogin";
import useGoogleLogin from "../../hooks/auth/useGoogleLogin";
import { SignInFormData } from "../../types/auth";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { DebouncedInput } from "../../components/DebouncedInput";
import logo from "../../images/aivy_logo3.png";

const SignIn = () => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<SignInFormData>();

  const { signIn, isLoading, apiError } = useSignIn();
  const {
    loginWithKakao,
    isLoading: isKakaoLoading,
    error: kakaoError,
  } = useKakaoLogin();

  const {
    loginWithGoogle,
    isLoading: isGoogleLoading,
    error: googleError,
  } = useGoogleLogin();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleNavigation = (path: string): void => {
    window.location.href = path;
  };

  const onSubmit = async (data: SignInFormData) => {
    await signIn(data, setError);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col font-serif-kr">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src={logo} alt="Aivy Logo" className="w-20 h-20" />
              <button
                onClick={() => handleNavigation("/")}
                className="h-12 text-4xl font-bold bg-gradient-to-r from-teal-600/60 via-green-500 to-emerald-600/60 bg-clip-text text-transparent font-serif-en"
              >
                Aivy AI
              </button>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 whitespace-pre-line">
              {`계정에 로그인하여 
              Aivy AI를 시작하세요`}
            </h2>
          </div>

          <div className="w-full max-w-[398px] mx-auto">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {apiError && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">
                  {apiError}
                </div>
              )}

              {kakaoError && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">
                  {kakaoError}
                </div>
              )}

              {googleError && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">
                  {googleError}
                  {googleError.includes("팝업") && (
                    <div className="mt-2 text-xs text-gray-600">
                      💡 팁: 브라우저 설정에서 팝업 차단을 해제해주세요
                    </div>
                  )}
                  {googleError.includes("FedCM") && (
                    <div className="mt-2 text-xs text-gray-600">
                      💡 팁: 주소창 왼쪽 아이콘에서 "타사 로그인 허용"을
                      클릭해주세요
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                  아이디
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <Controller
                    name="x_id"
                    control={control}
                    rules={{ required: "아이디를 입력해주세요" }}
                    render={({ field }) => (
                      <DebouncedInput
                        {...field}
                        type="text"
                        placeholder="아이디를 입력하세요"
                        className="pl-12"
                        delay={300}
                      />
                    )}
                  />
                </div>
                {errors.x_id && (
                  <p className="mt-2 text-sm text-red-600 text-left">
                    {errors.x_id.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                  비밀번호
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <Controller
                    name="app_key"
                    control={control}
                    rules={{ required: "비밀번호를 입력해주세요" }}
                    render={({ field }) => (
                      <DebouncedInput
                        {...field}
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-12 pr-12"
                        delay={300}
                      />
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.app_key && (
                  <p className="mt-2 text-sm text-red-600 text-left">
                    {errors.app_key.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => handleNavigation("/auth/find-id")}
                  className="text-sm md:text-base text-blue-600 hover:text-blue-700"
                >
                  계정을 잊었나요?
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigation("/auth/find-password")}
                  className="text-sm md:text-base text-blue-600 hover:text-blue-700"
                >
                  비밀번호를 잊으셨나요?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 h-[45px] bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-[6px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  "로그인"
                )}
              </button>

              {/* 구분선 */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">또는</span>
                </div>
              </div>

              {/* 소셜 로그인 버튼들 */}
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={loginWithKakao}
                  disabled={isKakaoLoading}
                  className="w-full h-[45px] bg-[#FEE500] hover:bg-[#FDD835] text-[#000000] text-[15px] font-medium rounded-[6px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border-[1px] border-[#F7E600]"
                  style={{
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  }}
                >
                  {isKakaoLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  ) : (
                    <>
                      <svg
                        className="w-[24px] h-[24px]"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 3C7.03125 3 3 6.50781 3 10.8047C3 13.5703 4.67969 16.0078 7.125 17.4141L6.09375 21.0469C5.98438 21.4219 6.39844 21.7031 6.75 21.4688L11.25 18.7969C11.4844 18.8203 11.7422 18.8203 12 18.8203C16.9688 18.8203 21 15.3125 21 10.8047C21 6.50781 16.9688 3 12 3Z"
                          fill="#3C1E1E"
                        />
                      </svg>
                      카카오 로그인
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={loginWithGoogle}
                  disabled={isGoogleLoading}
                  className="w-full h-[45px] bg-white hover:bg-gray-50 text-gray-700 text-[15px] font-medium rounded-[6px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border-[1px] border-gray-300"
                  style={{
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  }}
                >
                  {isGoogleLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  ) : (
                    <>
                      <svg className="w-[20px] h-[20px]" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      구글 로그인
                    </>
                  )}
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm md:text-base text-gray-600">
                  아직 계정이 없으신가요?{" "}
                  <button
                    type="button"
                    onClick={() => handleNavigation("/auth/signup")}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    회원가입
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
